import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Card, Divider, Dropdown, Popover, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  useDownloadAgentsExport,
  useDownloadSecondaryAgentsExport,
  useFilterAgents,
} from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { ExportFileType } from "../../../../common/enums";
import { Permission } from "../../../../common/security/enums";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { useCurrentAgentAccount, useHasPermissions, useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { AGENT_ROUTE_PATHS } from "../../paths";
import { AgentFilterPageRequest, AgentList } from "../../types";
import { formatAgentIdNumber } from "../../utils";
import { AgentTypeTagWithName } from "../tags/AgentTypeTagWithName";
import { SuperiorAgentsTable } from "./SuperiorAgentsTable";

type Props = {
  searchParams: AgentFilterPageRequest;
};

export const AgentListTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const hasAgentCommissionsPermission = useHasPermissions(Permission.AGENT_COMMISSIONS);
  const hasProfileCommissionsPermission = useHasPermissions(Permission.PROFILE_COMMISSIONS);
  const userAccount = useCurrentAgentAccount();

  const { data, isLoading } = useFilterAgents(searchParams);
  const downloadAgentsExport = useDownloadAgentsExport();
  const downloadSecondaryAgentsExport = useDownloadSecondaryAgentsExport();

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  const columns: ColumnsType<AgentList> = [
    {
      key: "aggregatedName",
      title: t("agent.attrs.aggregatedName"),
      width: 300,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <AgentTypeTagWithName
          textStyle={record.deactivated ? { color: cssVariables.colorRed } : undefined}
          agent={record}
          hideIdNumber
          ellipsis
          showLink
          showRepresentatives
        />
      ),
    },
    {
      key: "idNumber",
      title: t("agent.attrs.idNumber"),
      width: 110,
      render: (_, record) => formatAgentIdNumber(record),
    },
    {
      key: "identifier",
      title: (
        <>
          {t("agent.attrs.personalIdentificationNumber")} / {t("agent.attrs.companyRegistrationNumber")}
        </>
      ),
      width: 110,
      render: (_, record) => record.identifier,
    },
    {
      key: "commissionsSettingsLevel",
      title: t("agent.helpers.commissionsSettingsLevel"),
      width: 110,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.commissionsSettings.level?.code}</Ellipsis>,
    },
    {
      key: "commissionsSettingsRank",
      title: t("agent.helpers.commissionsSettingsRank"),
      width: 150,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.commissionsSettings.rank?.name}</Ellipsis>,
    },
    {
      key: "superiorAgents",
      width: 40,
      align: "center",
      render: (_, record) => (
        <Popover
          title={t("agent.titles.superiorAgents")}
          styles={{ root: { maxWidth: 580 } }}
          content={<SuperiorAgentsTable agent={record} />}
        >
          <span>
            <AntIcon color="blue" type="cluster" />
          </span>
        </Popover>
      ),
    },
    {
      key: "status",
      title: (
        <div>
          <div>{isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label")}</div>
          {isTopAgentPlus ? <div>{t("agent.helpers.agentSecondaryStatusSFAPlusLabel")}</div> : undefined}
        </div>
      ),
      width: 230,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <>
          <Ellipsis>{t(`agent.enums.agentStatus.${record.status}`)}</Ellipsis>

          {isTopAgentPlus && record.secondaryStatus ? (
            <>
              <br />
              <Ellipsis>{t(`agent.enums.agentSecondaryStatus.${record.secondaryStatus}`)}</Ellipsis>
            </>
          ) : undefined}
        </>
      ),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <div className="nowrap">
          <Dropdown
            menu={{
              items: [
                {
                  key: "show",
                  label: (
                    <ActionTextIcon
                      color="blue"
                      icon="eye"
                      text={t("common.show")}
                      path={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: record.id })}
                    />
                  ),
                },
                hasAgentCommissionsPermission &&
                (hasProfileCommissionsPermission || record.id !== userAccount?.agent.id)
                  ? {
                      key: "commissions",
                      label: (
                        <ActionTextIcon
                          color="green"
                          icon="euro"
                          text={t("common.commissions")}
                          path={generatePath(AGENT_ROUTE_PATHS.commissions.to, { id: record.id })}
                        />
                      ),
                    }
                  : null,
              ],
            }}
            trigger={["click"]}
          >
            <span>
              <ActionTextIcon icon="menu-unfold" color="blue" text={t("common.options")} />
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <Card className="card-box">
      <ComponentWithPermission permissions={[Permission.AGENT_EXPORT]}>
        <div className={data?.totalElementsCount ? "table-header-export-actions" : "margin-bottom-small"}>
          <ActionTextIcon
            icon="download"
            color="blue"
            text={t("common.exportXlsx")}
            onClick={() =>
              downloadAgentsExport.mutate({
                ...searchParams,
                exportFileType: ExportFileType.XLSX,
              })
            }
          />

          <Divider orientation="vertical" />

          <ActionTextIcon
            icon="download"
            color="green"
            text={t("common.exportCsv")}
            onClick={() =>
              downloadAgentsExport.mutate({
                ...searchParams,
                exportFileType: ExportFileType.CSV,
              })
            }
          />

          {isTopAgentPlus ? (
            <>
              <Divider orientation="vertical" />

              <ActionTextIcon
                icon="download"
                color="blue"
                text={t("common.exportXlsxProjectsPlus")}
                onClick={() =>
                  downloadSecondaryAgentsExport.mutate({
                    ...searchParams,
                    exportFileType: ExportFileType.XLSX,
                  })
                }
              />

              <Divider orientation="vertical" />

              <ActionTextIcon
                icon="download"
                color="green"
                text={t("common.exportCsvProjectsPlus")}
                onClick={() =>
                  downloadSecondaryAgentsExport.mutate({
                    ...searchParams,
                    exportFileType: ExportFileType.CSV,
                  })
                }
              />
            </>
          ) : undefined}
        </div>
      </ComponentWithPermission>

      <Table<AgentList>
        {...tableStandardProps()}
        loading={isLoading}
        columns={columns}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={data?.pageData}
        pagination={{
          ...data,
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};
